import { skipToken } from '@reduxjs/toolkit/query';
import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { LightTooltip } from 'components/tooltips/LightTooltip';
import { StoredRateSummary, useHourlyRates } from 'hooks/useHourlyRates';
import { CopyHourlyRatesToClipboard } from 'pages/pricelists/CopyHourlyRatesToClipboard';
import { HourlyRateDetailsCard } from 'pages/pricelists/HourlyRateDetailsCard';
import { employeeRatesOfCaseList } from 'pages/pricelists/modal/employeePricing';
import { inForceListOn } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { useToday } from 'pages/pricelists/modal/useEmployeePricing';
import { EMPLOYEE_CLIPBOARD_GROUP, employeeClipboardGroups } from 'pages/pricelists/util';
import React, { useMemo } from 'react';
import { useFindMatterRatesQuery } from 'store/api/matters';
import { JobRoleGroupType } from 'types/jobRoles';
import { HourlyRate } from 'types/pricelists';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

interface Props {
    matterId: number;
    rates: HourlyRate[] | undefined;
    stored: StoredRateSummary;
    pricelistCurrencyTypeCode: string | undefined;
}

/**
 * The invoice's per-case range of the case's list in force today and its prices behind the icon. A list priced
 * per person has no role rows on the invoice's read, so its people come from the case's own lists.
 */
export const CasePricelistRangeTooltip = React.memo(function CasePricelistRangeTooltip({
    matterId,
    rates,
    stored,
    pricelistCurrencyTypeCode,
}: Props) {
    const today = useToday();
    const hasRoleRates = !!rates && rates.length > 0;
    const { data: caseLists } = useFindMatterRatesQuery(hasRoleRates ? skipToken : String(matterId));
    const employeeRates = useMemo(
        () =>
            hasRoleRates ? undefined : employeeRatesOfCaseList(inForceListOn(caseLists ?? [], today)?.personalRates),
        [hasRoleRates, caseLists, today]
    );
    const { hourlyRateDetails, hourlyRateSummary } = useHourlyRates(rates, stored, employeeRates);
    const { classifiers } = useClassifiers({ codes: ['JOB_ROLE_GROUP_TYPE'] });

    const groupTitles = useMemo(
        () => ({
            [JobRoleGroupType.LEGAL]: getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.LEGAL),
            [JobRoleGroupType.ADMIN]: getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.ADMIN),
            [EMPLOYEE_CLIPBOARD_GROUP]: t('pricelists.employee.clipboard-group'),
        }),
        [classifiers]
    );

    if (!hasRoleRates && !employeeRates?.length) return null;

    const currency = pricelistCurrencyTypeCode ? `${pricelistCurrencyTypeCode} ` : '';

    return (
        <LightTooltip
            mode="click"
            title={<HourlyRateDetailsCard data={hourlyRateDetails} employeeRates={employeeRates} fontSize="0.8rem" />}
        >
            <span className="text-link ml-4 inline-flex cursor-pointer items-center whitespace-nowrap hover:underline">
                {currency}
                {hourlyRateSummary}
                <InfoOutlinedIcon className="text-grey-400 ml-[4px] h-[1.2rem]" />
                <CopyHourlyRatesToClipboard
                    data={hourlyRateDetails}
                    groupTitles={groupTitles}
                    extraGroups={employeeRates ? employeeClipboardGroups(employeeRates) : undefined}
                />
            </span>
        </LightTooltip>
    );
});
